import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Loader2, Save, ShieldCheck } from 'lucide-react';
import type { CommunicationsSettings } from '../../../models/MasterAdmin/communications';
import {
  commCardClass,
  commInputClass,
  commPrimaryBtnClass,
  commSecondaryBtnClass,
  commSectionDescClass,
  commSectionTitleClass,
  commSelectClass,
  commStickyFooterClass,
} from '../../../components/Communications/communicationsUi';
import {
  getCommunicationsSettings,
  testCommunicationsCredentials,
  updateCommunicationsSettings,
} from '../../../services/MasterAdmin/communicationsService';

const CommunicationsSettingsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [ivrEnabled, setIvrEnabled] = useState(false);
  const [executivePhoneNumber, setExecutivePhoneNumber] = useState('');
  const [successVoiceMessage, setSuccessVoiceMessage] = useState('');
  const [failureVoiceMessage, setFailureVoiceMessage] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiTokenConfigured, setApiTokenConfigured] = useState(false);
  const [accountSid, setAccountSid] = useState('');
  const [apiSubdomain, setApiSubdomain] = useState<'api.in.exotel.com' | 'api.exotel.com'>(
    'api.in.exotel.com'
  );
  const [exoPhone, setExoPhone] = useState('');
  const [webhooks, setWebhooks] = useState<{ ivrUrl: string | null; statusCallbackUrl: string | null }>({
    ivrUrl: null,
    statusCallbackUrl: null,
  });

  const applySettings = (data: CommunicationsSettings) => {
    setIvrEnabled(Boolean(data.ivrEnabled));
    setExecutivePhoneNumber(data.executivePhoneNumber || '');
    setSuccessVoiceMessage(data.successVoiceMessage || '');
    setFailureVoiceMessage(data.failureVoiceMessage || '');
    setApiKey(data.apiKey || '');
    setApiKeyConfigured(Boolean(data.apiKeyConfigured));
    setApiTokenConfigured(Boolean(data.apiTokenConfigured));
    setApiToken('');
    setAccountSid(data.accountSid || '');
    setApiSubdomain(data.apiSubdomain || 'api.in.exotel.com');
    setExoPhone(data.exoPhone || '');
    setWebhooks(data.webhooks || { ivrUrl: null, statusCallbackUrl: null });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCommunicationsSettings();
      applySettings(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load settings.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const payload: Record<string, unknown> = {
        ivrEnabled,
        executivePhoneNumber: executivePhoneNumber.trim() || null,
        successVoiceMessage: successVoiceMessage.trim() || null,
        failureVoiceMessage: failureVoiceMessage.trim() || null,
        accountSid: accountSid.trim() || null,
        apiSubdomain,
        exoPhone: exoPhone.trim() || null,
      };
      if (apiKey.trim() && !apiKey.includes('*')) payload.apiKey = apiKey.trim();
      if (apiToken.trim()) payload.apiToken = apiToken.trim();
      const data = await updateCommunicationsSettings(payload);
      applySettings(data);
      setSuccess('Settings saved.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestCredentials = async () => {
    try {
      setTesting(true);
      setError(null);
      setSuccess(null);

      if (!accountSid.trim()) {
        setError('Account SID is required.');
        return;
      }
      const keyFromForm = apiKey.trim() && !apiKey.includes('*');
      if (!keyFromForm && !apiKeyConfigured) {
        setError('API key is required.');
        return;
      }
      if (!apiToken.trim() && !apiTokenConfigured) {
        setError('API token is required. Enter your Exotel API token.');
        return;
      }

      await testCommunicationsCredentials({
        apiKey: apiKey.trim(),
        apiToken: apiToken.trim(),
        accountSid: accountSid.trim(),
        apiSubdomain,
      });
      setSuccess('Exotel credentials verified successfully.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Credential test failed.');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = async (value: string | null) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setSuccess('Copied to clipboard.');
    setTimeout(() => setSuccess(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center text-gray-500">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
          {success}
        </div>
      ) : null}

      <section className={commCardClass}>
        <h2 className={commSectionTitleClass}>Exotel API credentials</h2>
        <p className={commSectionDescClass}>
          Required for placing calls and fetching live call data. You can test before saving — all three fields must be filled.
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">API key</label>
            <input
              type="text"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Your Exotel API key"
              className={commInputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">API token</label>
            <input
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder={apiTokenConfigured ? '••••••••  (leave blank to keep)' : 'Your Exotel API token'}
              className={commInputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Account SID</label>
            <input
              type="text"
              value={accountSid}
              onChange={(e) => setAccountSid(e.target.value)}
              placeholder="vedikahealth"
              className={commInputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Region</label>
            <select
              value={apiSubdomain}
              onChange={(e) => setApiSubdomain(e.target.value as 'api.in.exotel.com' | 'api.exotel.com')}
              className={commSelectClass}
            >
              <option value="api.in.exotel.com">India (Mumbai)</option>
              <option value="api.exotel.com">Singapore</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">ExoPhone (Caller ID)</label>
            <input
              type="tel"
              value={exoPhone}
              onChange={(e) => setExoPhone(e.target.value)}
              placeholder="Your virtual number"
              className={commInputClass}
            />
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button type="button" onClick={() => void handleTestCredentials()} disabled={testing} className={commSecondaryBtnClass}>
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Test credentials
          </button>
        </div>
      </section>

      <section className={commCardClass}>
        <h2 className={commSectionTitleClass}>Webhook endpoints</h2>
        <p className={commSectionDescClass}>
          Configure these URLs in your Exotel dashboard or they will be used automatically for outbound calls.
        </p>
        <div className="mt-4 space-y-3">
          {[
            { label: 'IVR Passthru', value: webhooks.ivrUrl },
            { label: 'Status callback', value: webhooks.statusCallbackUrl },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{item.label}</p>
                <p className="truncate font-mono text-xs text-gray-800 dark:text-gray-200">{item.value || '—'}</p>
              </div>
              <button
                type="button"
                disabled={!item.value}
                onClick={() => void copyToClipboard(item.value)}
                className={commSecondaryBtnClass}
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className={commCardClass}>
        <h2 className={commSectionTitleClass}>Inbound IVR routing</h2>
        <p className={commSectionDescClass}>
          Controls how inbound Exotel IVR webhooks are processed. The IVR WhatsApp template is configured in WhatsApp
          → Settings.
        </p>
        <div className="mt-4 space-y-4">
          <label className="flex cursor-pointer items-center justify-between gap-4">
            <div>
              <span className="block text-sm font-medium text-gray-900 dark:text-white">IVR enabled</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                When disabled, webhooks are logged but no routing actions run.
              </span>
            </div>
            <input
              type="checkbox"
              checked={ivrEnabled}
              onChange={(e) => setIvrEnabled(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-primary-600"
            />
          </label>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Executive phone</label>
            <input
              type="tel"
              value={executivePhoneNumber}
              onChange={(e) => setExecutivePhoneNumber(e.target.value)}
              placeholder="10-digit mobile, e.g. 9876543210"
              className={commInputClass}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Your team member&apos;s mobile — this number rings first when you use Call back or Place a call.
              Not the same as ExoPhone above.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Success voice message</label>
            <textarea value={successVoiceMessage} onChange={(e) => setSuccessVoiceMessage(e.target.value)} rows={2} className={commInputClass} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Failure voice message</label>
            <textarea value={failureVoiceMessage} onChange={(e) => setFailureVoiceMessage(e.target.value)} rows={2} className={commInputClass} />
          </div>
        </div>
      </section>

      <div className={commStickyFooterClass}>
        <button type="button" onClick={() => void handleSave()} disabled={saving} className={commPrimaryBtnClass}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save settings
        </button>
      </div>
    </div>
  );
};

export default CommunicationsSettingsPanel;
